import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RotateCcw, Store } from "lucide-react";

const STORAGE_KEY = "sunrise-storefront-overrides-v1";

export const DEFAULT_STOREFRONTS = [
  { name: "COFFEE",   color: "#fb7185" },
  { name: "BAKERY",   color: "#fbbf24" },
  { name: "MARKET",   color: "#84cc16" },
  { name: "DELI",     color: "#22d3ee" },
  { name: "PHARMACY", color: "#a78bfa" },
  { name: "BARBER",   color: "#f97316" },
  { name: "CAFE",     color: "#10b981" },
  { name: "LAUNDRY",  color: "#3b82f6" },
];

export function loadStorefronts() {
  if (typeof window === "undefined") return DEFAULT_STOREFRONTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STOREFRONTS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== 8) return DEFAULT_STOREFRONTS;
    return parsed;
  } catch {
    return DEFAULT_STOREFRONTS;
  }
}

export function saveStorefronts(arr) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch { /* quota / privacy mode */ }
  window.dispatchEvent(new CustomEvent("storefronts-changed", { detail: arr }));
}

export default function StorefrontEditor() {
  const [stores, setStores] = useState(loadStorefronts);

  useEffect(() => { saveStorefronts(stores); }, [stores]);

  const update = (i, field, val) =>
    setStores((arr) => arr.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));

  const resetAll = () => setStores(DEFAULT_STOREFRONTS);

  return (
    <Card className="bg-[#0f172a] border-[#1f2937] p-5" data-testid="storefront-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-amber-300" />
          <div className="text-[11px] tracking-[0.3em] text-amber-300 font-bold">
            STOREFRONT EDITOR
          </div>
        </div>
        <Button
          onClick={resetAll}
          data-testid="storefront-reset-btn"
          variant="ghost"
          size="sm"
          className="text-[10px] tracking-[0.2em] text-[#94a3b8] hover:text-white hover:bg-[#1f2937] h-7 px-2"
        >
          <RotateCcw className="w-3 h-3 mr-1" /> RESET
        </Button>
      </div>

      <p className="text-[10px] text-[#94a3b8] mb-3 font-mono leading-relaxed">
        Rename and recolor the 8 retail storefronts. Edits sync to 3D, 2D plans, Site Plan, and PDF exports. Stored locally in your browser.
      </p>

      <div className="space-y-2">
        {stores.map((s, i) => (
          <div key={i} className="grid grid-cols-[28px_1fr_46px] gap-2 items-center" data-testid={`storefront-row-${i + 1}`}>
            <div className="text-[10px] font-mono text-[#5b6776] text-right">
              {String(i + 1).padStart(2, "0")}
            </div>
            <Input
              data-testid={`storefront-name-${i + 1}`}
              value={s.name}
              onChange={(e) => update(i, "name", e.target.value.toUpperCase().slice(0, 14))}
              className="h-7 text-[11px] tracking-[1.5px] bg-[#0b0f14] border-[#1f2937] text-[#e6edf3] font-mono px-2"
            />
            <input
              data-testid={`storefront-color-${i + 1}`}
              type="color"
              value={s.color}
              onChange={(e) => update(i, "color", e.target.value)}
              className="h-7 w-11 rounded cursor-pointer border border-[#1f2937] bg-[#0b0f14] p-0"
              aria-label={`Color for ${s.name}`}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
